import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

/**
 * ChangePasswordModal
 *
 * Props:
 *  - onClose: () => void
 *  - resetMode: boolean (default false)
 *      true  → no active session; sends a password-reset email via Supabase
 *      false → active session exists; updates password directly via updateUser
 *  - emailPlaceholder: string (optional; used in resetMode email field)
 */
const ChangePasswordModal = ({
  onClose,
  resetMode = false,
  emailPlaceholder = 'felix.gagnon@scann3r.ca',
}) => {
  const [form, setForm] = useState({ email: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  /* ── Reset-email flow (no session) ── */
  const handleResetEmail = async (e) => {
    e?.preventDefault();
    setError('');

    if (!form?.email?.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      // Must be listed in Supabase Auth → Redirect URLs.
      // Use the production reset page so GoTrue does not fall back to bare Site URL.
      const redirectTo = 'https://scann3r.ca/reset-password';
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        form.email.trim(),
        { redirectTo },
      );

      if (resetError) {
        const message = resetError.message || 'Failed to send reset email.';
        if (/rate limit|security purposes|over_email_send_rate_limit/i.test(message)) {
          setError('Too many reset emails requested. Please wait about a minute and try again.');
        } else {
          setError(message);
        }
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Direct update flow (active session) ── */
  const handleUpdatePassword = async (e) => {
    e?.preventDefault();
    setError('');

    if (!form?.newPassword?.trim()) {
      setError('Please enter a new password.');
      return;
    }
    if (form?.newPassword?.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (form?.newPassword !== form?.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: form?.newPassword,
      });
      if (updateError) {
        setError(updateError?.message || 'Failed to update password.');
      } else {
        setSuccess(true);
        setTimeout(() => onClose?.(), 2000);
      }
    } catch (err) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = resetMode ? handleResetEmail : handleUpdatePassword;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="KeyRound" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {resetMode ? 'Reset Password' : 'Change Password'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {resetMode ? 'We\'ll send a reset link to your email' : 'Update your account password'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-green-500" />
              </div>
              {resetMode ? (
                <>
                  <p className="text-sm font-medium text-foreground text-center">Reset email sent!</p>
                  <p className="text-xs text-muted-foreground text-center">
                    Check your inbox for a password reset link. Open it once to set a new password.
                  </p>
                  <Button variant="outline" size="sm" onClick={onClose} className="mt-2">
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">Password updated successfully!</p>
                  <p className="text-xs text-muted-foreground">This window will close shortly.</p>
                </>
              )}
            </div>
          ) : resetMode ? (
            /* ── Reset email form ── */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Your Email <span className="text-destructive">*</span>
                </label>
                <Input
                  type="email"
                  value={form?.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder={emailPlaceholder}
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon name="Mail" size={15} />
                      Send Reset Link
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            /* ── Direct update form ── */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  New Password <span className="text-destructive">*</span>
                </label>
                <Input
                  type="password"
                  value={form?.newPassword}
                  onChange={(e) => setForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
                  placeholder="••••••••"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password <span className="text-destructive">*</span>
                </label>
                <Input
                  type="password"
                  value={form?.confirmPassword}
                  onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Icon name="Save" size={15} />
                      Update Password
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
